import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { downloadReport } from "../download";

const K = ({ n, l, tone }) => <div className={`kpi ${tone || ""}`}><div className="n">{n ?? 0}</div><div className="l">{l}</div></div>;
const decClass = (d) => (/high-risk/i.test(d) ? "danger" : /personal/i.test(d) ? "warn" : "ok");

export default function Discovery() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const loadSummary = useCallback(() => api.get("/discovery/summary").then((r) => setSummary(r.data?.data)), []);
  const loadFindings = useCallback(() => {
    const params = {}; if (filter) params.decision = filter;
    return api.get("/discovery/findings", { params }).then((r) => setRows(r.data?.data || []));
  }, [filter]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadFindings(); }, [loadFindings]);

  const run = async (all) => {
    setBusy(true); setMsg("");
    try {
      const res = await api.post(`/discovery/classify${all ? "" : "?only=new"}`);
      const d = res.data?.data;
      setMsg(`${res.data?.message}. ${Object.entries(d?.tally || {}).map(([k, v]) => `${k}: ${v}`).join("  ·  ")}`);
      await loadSummary(); await loadFindings();
    } catch (e) { setMsg(e?.response?.data?.message || "Classification failed"); }
    setBusy(false);
  };

  const s = summary || {};
  return (
    <div>
      <h1>Data Discovery & Classification</h1>
      <p className="sub">Layered classification of agent-discovered PII — source, field name, value pattern & exclusion rules combine into one confidence score.</p>

      <div className="kpis">
        <K n={s.total} l="Total Findings" />
        <K n={s.unclassified} l="Unclassified" tone="warn" />
        <K n={(s.byClassification || []).find((x) => x.label === "High-Risk Personal Data")?.value} l="High-Risk PII" tone="danger" />
        <K n={(s.byClassification || []).find((x) => x.label === "Personal Data")?.value} l="Personal Data" tone="warn" />
        <K n={(s.byClassification || []).find((x) => x.label === "IT Asset")?.value} l="IT Asset (excluded)" tone="ok" />
        <K n={(s.byEncryption || []).find((x) => x.label === "PLAINTEXT")?.value} l="Plaintext" tone="danger" />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="toolbar">
          <button className="btn primary" disabled={busy} onClick={() => run(false)}>{busy ? "Classifying…" : "Classify new findings"}</button>
          <button className="btn" disabled={busy} onClick={() => run(true)}>Re-classify all</button>
          <div style={{ flex: 1 }} />
          <Link className="btn sm" to="/m/classification-rules">Edit rules / dictionaries</Link>
          <button className="btn sm" onClick={() => downloadReport("pii-findings", "xlsx")}>Excel</button>
          <button className="btn sm" onClick={() => downloadReport("pii-findings", "pdf")}>PDF</button>
        </div>
        {msg && <div style={{ padding: "8px 12px", color: "#0f766e", fontSize: 13 }}>{msg}</div>}
      </div>

      <div className="card">
        <div className="toolbar">
          <select style={{ maxWidth: 240 }} value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">All decisions</option>
            <option>Personal Data</option><option>Needs Review</option><option>Not Personal Data</option>
          </select>
          <span style={{ color: "#64748b", fontSize: 13 }}>{rows.length} findings</span>
        </div>
        <div className="tablewrap">
          <table>
            <thead><tr><th>Host</th><th>Location</th><th>Data Type</th><th>Classification</th><th>Decision</th><th>Score</th><th>Encryption</th><th>Scanned</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.host || "—"}</td>
                  <td style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }}>{r.location || "—"}</td>
                  <td>{r.data_type || "—"}</td>
                  <td>{r.classification ? <span className={`pill ${decClass(r.classification)}`}>{r.classification}</span> : <span className="pill">unclassified</span>}</td>
                  <td>{r.decision || "—"}</td>
                  <td><b>{r.score ?? "—"}</b></td>
                  <td><span className={`pill ${r.encryption_status === "PLAINTEXT" ? "danger" : r.encryption_status === "ENCRYPTED" ? "ok" : ""}`}>{r.encryption_status}</span></td>
                  <td>{r.scanned_at ? new Date(r.scanned_at).toLocaleDateString("en-GB") : "—"}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={8}><div className="empty">No findings yet. The ensmap-agent posts them to /dpdp/pii-discovery/findings.</div></td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
